import { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [role, setRole] = useState(localStorage.getItem('role') || 'student');
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const clearSession = () => {
    setRole('student');
    setUser(null);
    localStorage.removeItem('role');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
  };

  // Sync local role/user state with the actual authenticated user.
  // If there's no matching tbluser row, the account has no valid app
  // profile, so any stale cached role/user must not be trusted.
  const syncUserFromAuth = async (authUser) => {
    if (!authUser) {
      clearSession();
      return;
    }

    const { data: userData } = await supabase
      .from('tbluser')
      .select('id, username, firstname, lastname, middlename, email, role')
      .eq('auth_id', authUser.id)
      .single();

    if (userData) {
      setUser(userData);
      setRole(userData.role);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('role', userData.role);
    } else {
      await supabase.auth.signOut();
      clearSession();
    }
  };

  // Check if user is already logged in on mount, and keep role/user in
  // sync with the live Supabase session afterward (sign-outs, expired or
  // rotated refresh tokens, another account signing in on the same
  // browser, etc.) instead of trusting a possibly stale cached role.
  useEffect(() => {
    const checkUser = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      await syncUserFromAuth(authUser);
    };

    checkUser();

    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        clearSession();
        return;
      }
      syncUserFromAuth(session?.user ?? null);
    });

    return () => subscription.subscription.unsubscribe();
  }, []);

  const login = (userRole, userData) => {
    setRole(userRole);
    setUser(userData);
    localStorage.setItem('role', userRole);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('isAuthenticated', 'true');
  };

  const updateUser = (userData) => {
    const nextUser = { ...user, ...userData };
    setUser(nextUser);
    if (nextUser.role) {
      setRole(nextUser.role);
      localStorage.setItem('role', nextUser.role);
    }
    localStorage.setItem('user', JSON.stringify(nextUser));
  };

  const logout = async () => {
    await supabase.auth.signOut();
    clearSession();
  };

  return (
    <AuthContext.Provider value={{ role, user, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
